import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { COLOR_SYSTEM } from "components/design/design-system";
import Icon from "components/atoms/Icon";
import Text from "components/atoms/Text";
import { useSelector } from "react-redux";
import { RootState } from "store";

// 사이드바 메뉴 항목 타입 정의
export interface SidebarMenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  requireAuth?: boolean;
  requireAdmin?: boolean; // 관리자 전용 메뉴 여부
  roles?: string[]; // 접근 가능한 역할 목록
}

interface SidebarProps {
  items: SidebarMenuItem[];
  isOpen?: boolean;
  onToggle?: () => void;
  /** 선택 — 메뉴 이동 전 가드(false면 이동하지 않는다). 없으면 동작 불변(D-19). */
  onBeforeNavigate?: (path: string) => boolean;
}

// 사이드바 컨테이너 스타일
const SidebarContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: ${({ isOpen }) => (isOpen ? "250px" : "60px")};
  height: calc(100vh - 103px);
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  overflow-x: hidden;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

// 사이드바 헤더 스타일
const SidebarHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "space-between" : "center")};
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 로고 컨테이너 스타일
const LogoContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

// 메뉴 목록 스타일
const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

// 메뉴 항목 스타일
const MenuItem = styled.li<{ isActive: boolean; isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isOpen }) => (isOpen ? "flex-start" : "center")};
  padding: 12px 16px;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? COLOR_SYSTEM.get("Blue")?.[50] : "transparent"};

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

// 메뉴 아이콘 스타일
const MenuIcon = styled.div<{ isOpen: boolean }>`
  margin-right: ${({ isOpen }) => (isOpen ? "16px" : "0")};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

// 메뉴 레이블 스타일
const MenuLabel = styled.div<{ isOpen: boolean }>`
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  white-space: nowrap;
  transition: opacity 0.2s ease;
`;

// 토글 버튼 스타일
const ToggleButton = styled.button<{ isOpen: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: ${({ isOpen }) => (isOpen ? "0" : "-16px")};

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
    border-radius: 4px;
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ items, isOpen = false, onToggle, onBeforeNavigate }) => {
  const [open, setOpen] = useState(isOpen);
  const navigate = useNavigate();
  const location = useLocation();

  // 사용자 정보 가져오기
  const { user } = useSelector((state: RootState) => state.user);

  // 사용자 권한에 따라 메뉴 필터링
  const filteredItems = items.filter((item) => {
    // 로그인이 필요한 메뉴이고 로그인하지 않은 경우 제외
    if (item.requireAuth && !user) {
      return false;
    }

    // 관리자 권한이 필요한 메뉴이고 관리자가 아닌 경우 제외
    if (item.requireAdmin && (!user?.roles || !user.roles.includes("admin"))) {
      return false;
    }

    return true;
  });

  // 외부에서 isOpen prop이 변경되면 내부 상태도 업데이트
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  // 사이드바 토글 함수
  const handleToggle = () => {
    const newState = !open;
    setOpen(newState);
    if (onToggle) {
      onToggle();
    }
  };

  // 메뉴 항목 클릭 핸들러 — 가드가 false면 이동 보류(상세가 배너, D-19). 가드 없으면 동작 불변.
  const handleMenuClick = (path: string) => {
    if (onBeforeNavigate && !onBeforeNavigate(path)) return;
    navigate(path);
  };

  // AI 평가 관련 메뉴 ID 확인
  const isAIEvaluationMenu = (id: string) => {
    return ["ai-review-test-gt", "ai-review-test-results", "ai-review-test-analysis"].includes(id);
  };

  // 약물관리 메뉴 ID 확인
  const isMedicationMenu = (id: string) => {
    return id === "medications" || id === "health-functional-foods";
  };

  return (
    <SidebarContainer isOpen={open}>
      <SidebarHeader isOpen={open}>
        <LogoContainer isOpen={open}>
          <Text textType="H4" color={COLOR_SYSTEM.get("Blue")?.[500]}>
            YMYD
          </Text>
        </LogoContainer>
        <ToggleButton isOpen={open} onClick={handleToggle}>
          <Icon name={open ? "Chevron Left" : "Chevron Right"} size={20} />
        </ToggleButton>
      </SidebarHeader>
      <MenuList>
        {filteredItems.map((item) => (
          <MenuItem
            key={item.id}
            isActive={location.pathname === item.path}
            isOpen={open}
            onClick={() => handleMenuClick(item.path)}
          >
            <MenuIcon isOpen={open}>
              <Icon 
                name={item.icon} 
                size={20} 
                color={
                  isAIEvaluationMenu(item.id) ? COLOR_SYSTEM.get("Green")?.[500] : 
                  isMedicationMenu(item.id) ? COLOR_SYSTEM.get("Blue")?.[500] : 
                  undefined
                }
              />
            </MenuIcon>
            <MenuLabel isOpen={open}>
              <Text 
                textType="Body2"
                color={
                  isAIEvaluationMenu(item.id) ? COLOR_SYSTEM.get("Green")?.[500] : 
                  isMedicationMenu(item.id) ? COLOR_SYSTEM.get("Blue")?.[500] : 
                  undefined
                }
              >
                {item.label}
              </Text>
            </MenuLabel>
          </MenuItem>
        ))}
      </MenuList>
    </SidebarContainer>
  );
};

export default Sidebar;
